import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Container,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { ROLES } from "@/utils/constants";
import Icons from "@/assets";
import { getApiErrorMessage } from "@/utils/helper";

const StyledContainer = styled(Container)(() => ({
  backgroundColor: "#F0EAFF",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "5vw",
}));

const LogoContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "8vw",
  marginTop: "14vw",
}));

const StyledButton = styled(Button)(() => ({
  fontWeight: 700,
  fontSize: "4vw",
  lineHeight: "130%",
  letterSpacing: "0%",
  textAlign: "center",
  textTransform: "none",
  borderRadius: "6vw",
  padding: "3vw 8vw",
  marginTop: "6vw",
  backgroundImage: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
  color: "#fff",
  width: "80%",
  "&:hover": {
    backgroundImage: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    boxShadow: "0 1vw 3vw rgba(157, 121, 244, 0.4)",
  },
  "&.Mui-disabled": {
    background: "#ccc",
    color: "#999",
  },
  "@media (min-width: 600px)": {
    fontSize: "1.5vw",
    borderRadius: "2vw",
    padding: "1vw 3vw",
    marginTop: "2vw",
  },
}));

const StyledTextField = styled(TextField)(() => ({
  marginBottom: "4vw",
  backgroundColor: "rgba(255, 255, 255, 1)",
  borderRadius: "6vw",
  border: "0.25vw solid rgba(217, 217, 217, 1)",
  "& .MuiOutlinedInput-root": {
    borderRadius: "6vw",
    "& fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
  },
  "& .MuiInputBase-input": {
    padding: "3vw 4vw",
    fontSize: "3.5vw",
  },
  "@media (min-width: 600px)": {
    marginBottom: "1.5vw",
    borderRadius: "2vw",
    border: "0.1vw solid rgba(217, 217, 217, 1)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "2vw",
    },
    "& .MuiInputBase-input": {
      padding: "1vw 1.5vw",
      fontSize: "1.2vw",
    },
  },
}));

const ForgotPassword = () => {
  const [identifier, setIdentifier] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { api } = useApiClient();

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (isCooldown || isLoading) return;

    if (!identifier.trim()) {
      toast.error("Please enter your username or email ID.");
      return;
    }

    setIsLoading(true);
    setIsCooldown(true);
    
    try {
      const response = await api.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        identifier: identifier.trim(),
        role_name: ROLES.CONTRIBUTOR,
      });
      if (response?.data) {
        setIsSuccess(true);
        toast.success(response.data.message || "Reset link sent to your email!");
      }
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(error, "Failed to process request. Try again.")
      );
    } finally {
      setIsLoading(false);
      debounceTimeoutRef.current = setTimeout(() => {
        setIsCooldown(false);
      }, 5000);
    }
  };

  return (
    <StyledContainer maxWidth={false}>
      <LogoContainer>
        <Box
          component="img"
          src={Icons.WheelChair}
          alt="Wheelchair"
          sx={{
            width: { xs: "64.81vw", sm: "16vw" },
            height: { xs: "64.81vw", sm: "16vw" },
            marginBottom: "4vw",
          }}
        />
        <img
          src={Icons.Logo}
          alt="logo"
          style={{ height: "13vw", width: "42vw", maxWidth: "160px", maxHeight: "50px" }}
        />
      </LogoContainer>

      <Box sx={{ width: "100%", maxWidth: "400px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {isSuccess ? (
          <>
            <Typography
              sx={{
                fontSize: { xs: "5vw", sm: "1.8vw" },
                fontWeight: 600,
                color: "#1a1a1a",
                textAlign: "center",
                mb: "4vw",
              }}
            >
              Check your Email
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "3.5vw", sm: "1.2vw" },
                color: "#666",
                textAlign: "center",
                mb: "6vw",
              }}
            >
              We have sent a password reset link to your email address. Please check your inbox.
            </Typography>
            <StyledButton onClick={() => navigate("/")} variant="contained">
              Back to Login
            </StyledButton>
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: { xs: "5vw", sm: "1.8vw" },
                fontWeight: 500,
                color: "#1a1a1a",
                textAlign: "center",
                mb: "6vw",
              }}
            >
              Forgot Password
            </Typography>
            
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: { xs: "3.5vw", sm: "1.2vw" },
                  color: "rgba(0, 0, 0, 1)",
                  mb: "2vw",
                  ml: "2vw",
                }}
              >
                Username or Email ID
              </Typography>
              <StyledTextField
                fullWidth
                variant="outlined"
                value={identifier}
                placeholder="Enter username or email"
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={isLoading}
              />
            </Box>

            <StyledButton
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading || isCooldown}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Send Reset Link"
              )}
            </StyledButton>

            <Typography
              sx={{
                color: "#9068f6",
                fontSize: { xs: "3.5vw", sm: "1.2vw" },
                cursor: "pointer",
                mt: "6vw",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => navigate("/")}
            >
              Back to Login
            </Typography>
          </>
        )}
      </Box>
    </StyledContainer>
  );
};

export default ForgotPassword;
